import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Database, BrainCircuit, Clock, Folder, RefreshCw, CheckCircle2, AlertTriangle,
  XCircle, Activity, Users, FileText, Wallet, Bell,
} from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  database: Database,
  brain: BrainCircuit,
  clock: Clock,
  folder: Folder,
};

const STATUS_META = {
  ok:    { icon: CheckCircle2, color: "text-emerald-600",  bg: "bg-emerald-50 border-emerald-300",  label: "OK" },
  warn:  { icon: AlertTriangle, color: "text-amber-600",   bg: "bg-amber-50 border-amber-300",      label: "Warnung" },
  error: { icon: XCircle,       color: "text-rose-600",    bg: "bg-rose-50 border-rose-300",        label: "Fehler" },
};

export default function SystemHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/system/health");
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const overall = data?.overall_status || "ok";
  const overallMeta = STATUS_META[overall] || STATUS_META.ok;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto" data-testid="system-health-page">
      {/* Breadcrumb */}
      <div className="text-xs text-prime uppercase tracking-widest">/ Direktion / System Health</div>

      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-stone-900">System Health</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Echtzeit-Statusübersicht aller kritischen Komponenten des Wertia CRM.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="health-autorefresh-toggle"
          >
            <Activity className="w-4 h-4 mr-1.5" />
            Auto-Refresh {autoRefresh ? "ein" : "aus"}
          </Button>
          <Button
            size="sm"
            onClick={load}
            disabled={loading}
            className="bg-prime hover:bg-prime/90 text-white"
            data-testid="health-refresh-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Overall status banner */}
      <div className={`border-2 ${overallMeta.bg} rounded-lg p-5 flex items-center justify-between gap-4`} data-testid="health-overall-banner">
        <div className="flex items-center gap-4">
          <overallMeta.icon className={`w-10 h-10 ${overallMeta.color} shrink-0`} strokeWidth={1.8} />
          <div>
            <div className={`text-2xl font-display font-semibold ${overallMeta.color}`}>
              {overall === "ok" ? "Alle Systeme operativ" : overall === "warn" ? "Aufmerksamkeit erforderlich" : "Kritischer Fehler"}
            </div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">
              {data?.checked_at ? `Geprüft: ${new Date(data.checked_at).toLocaleString("de-CH")}` : "Lade…"}
            </div>
          </div>
        </div>
        <Badge variant="outline" className={`${overallMeta.color} text-base px-3 py-1.5 uppercase tracking-wider font-bold`}>
          {overallMeta.label}
        </Badge>
      </div>

      {/* Component cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="health-components-grid">
        {(data?.components || []).map((c) => {
          const Icon = ICONS[c.icon] || Activity;
          const meta = STATUS_META[c.status] || STATUS_META.ok;
          return (
            <Card key={c.name} className={`border ${meta.bg.replace("bg-", "border-").split(" ")[1] || "border-stone-200"}`} data-testid={`health-component-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 ${meta.bg} rounded-md flex items-center justify-center shrink-0`}>
                  <Icon className={`w-5 h-5 ${meta.color}`} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display text-base text-stone-900 truncate">{c.label || c.name}</h3>
                    <meta.icon className={`w-4 h-4 ${meta.color} shrink-0`} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{c.details}</p>
                  <p className="text-[10px] text-stone-400 mt-1 font-mono uppercase tracking-wider">{c.name}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* DB counts */}
      {data?.counts && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-display text-base text-stone-900 mb-3 flex items-center gap-2">
              <Database className="w-4 h-4 text-prime" /> Datenbestand
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <CountTile icon={Users}    label="Kunden"        value={data.counts.clients} />
              <CountTile icon={FileText} label="Policen"       value={data.counts.policies} />
              <CountTile icon={FileText} label="Anträge"       value={data.counts.antraege} />
              <CountTile icon={Wallet}   label="Provisionen"   value={data.counts.commissions} />
              <CountTile icon={Users}    label="Benutzer"      value={data.counts.users} />
              <CountTile icon={Bell}     label="Ungelesen"     value={data.counts.notifications_unread} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CountTile({ icon: Icon, label, value }) {
  return (
    <div className="border border-stone-200 rounded-md px-3 py-2.5 bg-stone-50/50">
      <div className="flex items-center gap-2 text-xs text-stone-500 uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-display font-semibold text-stone-900 mt-1 font-mono">{value ?? "—"}</div>
    </div>
  );
}
